import * as PIXI from 'pixi.js';
import { LayoutContainer, LayoutText } from '@pixi/layout/components'; // eslint-disable-line
import { APP_WIDTH, APP_HEIGHT, CARD_HEIGHT, CARD_WIDTH } from '../../utils';

import TappableCard from '../../common/containers/TappableCard';
import ColorFiltersRow, { cardMatchesColorFilter } from '../../common/containers/ColorFiltersRow';
import CostFiltersRow, { cardMatchesCostFilter } from '../../common/containers/CostFiltersRow';
import CardInfoContainer from '../../gameplay/containers/cardInfoContainer';

const TOP_BAR_HEIGHT = 120;
const TOP_HALF_HEIGHT = 615;
const LEADER_AREA_WIDTH = 350;
const DECK_CARDS_LIST_CONTAINER_WIDTH = APP_WIDTH - LEADER_AREA_WIDTH;
const BOTTOM_HALF_HEIGHT = APP_HEIGHT - TOP_BAR_HEIGHT - TOP_HALF_HEIGHT;
const QUICK_FILTERS_HEIGHT = 165;
const QUICK_FILTERS_BOTTOM_BORDER_HEIGHT = 10;
const COLLECTION_CARDS_LIST_CONTAINER_HEIGHT = BOTTOM_HALF_HEIGHT - (QUICK_FILTERS_HEIGHT + QUICK_FILTERS_BOTTOM_BORDER_HEIGHT);
const MAX_COPIES_PER_CARD = 4;

export default class DeckEditorManager {
    constructor(editMode = 'create', isLocked = false) {
        this.container = new PIXI.Container();
        this.container.label = 'deckEditorContainer';
        this.editMode = editMode;
        this.collection = [];
        this.mode = 'loading';
        this.isLocked = isLocked;

        this.name = `Deck ${Math.random().toString(36).substring(2, 8)}`;
        this.selectedLeader = null;
        this.deckCards = {};
        this.selectedColors = new Set();
        this.selectedCosts = new Set();
    }

    static async loadAssets() {
        await PIXI.Assets.load([
            { src: '/assets/decks/edit_deck_name.png', alias: 'editDeckName' },
            { src: '/assets/decks/save.png', alias: 'saveDeck' },
            { src: '/assets/decks/cancel.png', alias: 'cancelDeck' },
            { src: '/assets/decks/confirm_leader.png', alias: 'confirmLeader' },
            { src: '/assets/decks/swap_leader.png', alias: 'swapLeader' },
            { src: '/assets/decks/attention.png', alias: 'attentionIcon' },
            { src: '/assets/decks/attention_chopper.png', alias: 'attentionChopperIcon' },
            { src: '/assets/decks/cancel_swap.png', alias: 'cancelSwap' },
            { src: '/assets/decks/confirm_swap.png', alias: 'confirmSwap' },
            { src: '/assets/vs_screen/leader_backface.png', alias: 'leaderBackface' },
            { src: '/assets/card_info/leader_icon.png', alias: 'card_info_leader_icon' },
            { src: '/assets/card_info/character_icon.png', alias: 'card_info_character_icon' },
            { src: '/assets/card_info/stage_icon.png', alias: 'card_info_stage_icon' },
            { src: '/assets/card_info/event_icon.png', alias: 'card_info_event_icon' },
            { src: '/assets/card_info/waves_footer.png', alias: 'waves_footer' },
            { src: '/assets/card_info/close_card_info_x.png', alias: 'close_card_info_x' },
        ]);

        await PIXI.Assets.load({
            src: 'https://fonts.gstatic.com/s/opensans/v44/memvYaGs126MiZpBA-UvWbX2vVnXBbObj2OVTS-muw.woff2',
            data: {
                family: 'OpenSans',
                weights: ['600'],
                unicodeRange: 'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD',
            }
        });
    }

    attachToPixiApp(app) {
        app.stage.addChild(this.container);
    }

    detachFromPixiApp(app) {
        app.stage.removeChild(this.container);
    }

    buildLoadingPage() {
        this.container.removeChildren();

        const loadingPage = new LayoutContainer();
        loadingPage.label = 'loadingPage';
        loadingPage.layout = {
            width: APP_WIDTH,
            height: APP_HEIGHT,
        };

        const loadingText = new LayoutText({
            text: 'Loading...',
            style: {
                fontSize: 100,
                fill: 'white',
            }
        });
        loadingText.label = 'loadingText';
        loadingText.layout = {
            width: APP_WIDTH,
            objectPosition: 'center',
        };

        loadingPage.addChild(loadingText);

        this.container.addChild(loadingPage);
    }

    #createButton(width = 100, height = 100, bgColor = '#000000', text = '', fontSize = 50) {
        const buttonContainer = new PIXI.Container();

        const buttonGraphics = new PIXI.Graphics();
        buttonGraphics.roundRect(0, 0, width, height, 10);
        buttonGraphics.fill({ color: bgColor });
        buttonGraphics.stroke({ color: 'black', width: 6 });
        buttonContainer.addChild(buttonGraphics);

        const buttonText = new PIXI.Text({
            text: text,
            style: {
                fontFamily: 'Arial',
                fontSize: fontSize,
                fill: '#ffffff',
                fontWeight: 'bold'
            }
        });
        buttonText.x = width / 2;
        buttonText.y = height / 2;
        buttonText.anchor.set(0.5);
        buttonContainer.addChild(buttonText);

        return buttonContainer;
    }

    buildConfirmSwapModal() {
        const MODAL_BOX_WIDTH = 850;
        const MODAL_BOX_HEIGHT = 550;
        const MODAL_BOX_HEADER_HEIGHT = 130;
        const MODAL_BOX_RADIUS = 40;
        const MODAL_BOX_BODY_BORDER_WIDTH = 5;

        const confirmSwapModalContainer = new LayoutContainer();
        confirmSwapModalContainer.label = 'confirmSwapModalContainer';
        confirmSwapModalContainer.layout = {
            width: APP_WIDTH,
            height: APP_HEIGHT,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 50,
        };
        confirmSwapModalContainer.visible = false;
        confirmSwapModalContainer.eventMode = 'static';

        const modalBoxContainer = new PIXI.Container();
        modalBoxContainer.layout = {
            width: MODAL_BOX_WIDTH,
            height: MODAL_BOX_HEIGHT,
            flexDirection: 'column',
        };
        confirmSwapModalContainer.addChild(modalBoxContainer);

        const modalBoxMask = new PIXI.Graphics();
        modalBoxMask.roundRect(0, 0, MODAL_BOX_WIDTH, MODAL_BOX_HEIGHT, 40);
        modalBoxMask.fill({ color: 'white' });
        modalBoxContainer.addChild(modalBoxMask);
        modalBoxContainer.mask = modalBoxMask;

        const modalBoxHeader = new PIXI.Container();
        modalBoxHeader.layout = {
            width: MODAL_BOX_WIDTH,
            height: MODAL_BOX_HEADER_HEIGHT,
            flexDirection: 'row',
            alignItems: 'center',
            paddingLeft: 40,
            gap: 20,
        };
        modalBoxHeader.zIndex = 1;
        modalBoxContainer.addChild(modalBoxHeader);

        const modalBoxHeaderGradient = new PIXI.FillGradient({
            type: 'linear',
            start: { x: 0, y: 0.5 },
            end: { x: 1, y: 0.5 },
            colorStops: [
                { offset: 0, color: '#2a74a9' },
                { offset: 1, color: '#e9e9e9' },
            ],
        });

        const modalBoxHeaderBackground = new PIXI.Graphics();
        modalBoxHeaderBackground.rect(0, 0, MODAL_BOX_WIDTH, MODAL_BOX_HEADER_HEIGHT);
        modalBoxHeaderBackground.fill(modalBoxHeaderGradient);
        modalBoxHeader.addChild(modalBoxHeaderBackground);

        const attentionIcon = new PIXI.Sprite(PIXI.Assets.get('attentionIcon'));
        attentionIcon.layout = {
            width: 100,
            height: 100,
        };
        modalBoxHeader.addChild(attentionIcon);

        const modalBoxHeaderText = new PIXI.Text({
            text: 'Attention',
            style: {
                fontFamily: 'Open Sans',
                fontSize: 60,
                fontWeight: '700',
                fill: '#ffffff',
            },
        });
        modalBoxHeaderText.layout = {
            width: 400,
            height: 60,
            objectPosition: 'left',
        };
        modalBoxHeader.addChild(modalBoxHeaderText);

        const modalBoxBody = new PIXI.Container();
        modalBoxBody.layout = {
            width: MODAL_BOX_WIDTH,
            height: MODAL_BOX_HEIGHT - MODAL_BOX_HEADER_HEIGHT,
        };
        modalBoxContainer.addChild(modalBoxBody);

        const modalBoxBodyBackground = new PIXI.Graphics();
        modalBoxBodyBackground.roundRect(MODAL_BOX_BODY_BORDER_WIDTH / 2, -MODAL_BOX_RADIUS, MODAL_BOX_WIDTH - MODAL_BOX_BODY_BORDER_WIDTH, MODAL_BOX_HEIGHT - MODAL_BOX_HEADER_HEIGHT + (MODAL_BOX_RADIUS - MODAL_BOX_BODY_BORDER_WIDTH / 2), MODAL_BOX_RADIUS);
        modalBoxBodyBackground.fill({ color: '#e9e9e9' });
        modalBoxBodyBackground.stroke({ color: '#c3c3c5', width: MODAL_BOX_BODY_BORDER_WIDTH });
        modalBoxBody.addChild(modalBoxBodyBackground);

        const chopperMask = new PIXI.Graphics();
        chopperMask.roundRect(MODAL_BOX_BODY_BORDER_WIDTH, -MODAL_BOX_RADIUS, MODAL_BOX_WIDTH - MODAL_BOX_BODY_BORDER_WIDTH*2, MODAL_BOX_HEIGHT - MODAL_BOX_HEADER_HEIGHT + (MODAL_BOX_RADIUS - MODAL_BOX_BODY_BORDER_WIDTH), MODAL_BOX_RADIUS);
        chopperMask.fill({ color: 'white' });
        modalBoxBody.addChild(chopperMask);

        const attentionChopperIcon = new PIXI.Sprite(PIXI.Assets.get('attentionChopperIcon'));
        attentionChopperIcon.x = -20;
        attentionChopperIcon.mask = chopperMask;
        modalBoxBody.addChild(attentionChopperIcon);

        const modalBoxBodyText = new LayoutText({
            text: 'Switching the leader will reset the deck. Proceed?',
            style: {
                fontFamily: 'Open Sans',
                fontSize: 40,
                fontWeight: '600',
                fill: '#000000',
                wordWrap: true,
            },
        });
        modalBoxBodyText.layout = {
            width: MODAL_BOX_WIDTH - 300,
            height: 100,
            position: 'absolute',
            top: 90,
            right: 20,
        };
        modalBoxBody.addChild(modalBoxBodyText);

        const buttonsContainer = new LayoutContainer();
        buttonsContainer.layout = {
            width: MODAL_BOX_WIDTH,
            height: 107,
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 20,
            position: 'absolute',
            bottom: 40,
        };
        modalBoxBody.addChild(buttonsContainer);

        const cancelSwapButton = new PIXI.Sprite(PIXI.Assets.get('cancelSwap'));
        cancelSwapButton.layout = {
            width: 363,
            height: 107,
        };
        buttonsContainer.addChild(cancelSwapButton);
        cancelSwapButton.eventMode = 'static';
        cancelSwapButton.on('pointerdown', () => {
            this.hideConfirmSwapModal();
        });

        const confirmSwapButton = new PIXI.Sprite(PIXI.Assets.get('confirmSwap'));
        confirmSwapButton.layout = {
            width: 363,
            height: 107,
        };
        buttonsContainer.addChild(confirmSwapButton);
        confirmSwapButton.eventMode = 'static';
        confirmSwapButton.on('pointerdown', () => {
            this.swapLeader();
            this.hideConfirmSwapModal();
        });

        this.container.addChild(confirmSwapModalContainer);
    }

    showConfirmSwapModal() {
        const confirmSwapModalContainer = this.container.getChildByLabel('confirmSwapModalContainer');
        confirmSwapModalContainer.visible = true;
    }

    hideConfirmSwapModal() {
        const confirmSwapModalContainer = this.container.getChildByLabel('confirmSwapModalContainer');
        confirmSwapModalContainer.visible = false;
    }

    buildTopBar() {
        const deckNameContainer = new PIXI.Container();
        deckNameContainer.label = 'deckNameContainer';
        deckNameContainer.layout = {
            width: 500,
            height: 70,
            left: 50,
            position: 'absolute',
            alignItems: 'center',
        };
        this.topBarContainer.addChild(deckNameContainer);

        deckNameContainer.eventMode = 'static';
        deckNameContainer.on('pointerdown', () => {
            if (!this.isLocked) {
                this.onChangeDeckNameClicked?.(this.name);
            }
        });

        const deckNameBackground = new PIXI.Graphics();
        deckNameBackground.label = 'deckNameBackground';
        deckNameBackground.x = 0;
        deckNameBackground.y = 0;
        deckNameBackground.roundRect(0, 0, 500, 70, 50);
        deckNameBackground.fill({ color: '#0d1119' });
        deckNameBackground.stroke({ color: '#6d7e99', width: 3 });
        deckNameContainer.addChild(deckNameBackground); 

        const deckNameText = new LayoutText({
            text: this.name,
            style: {
                fontFamily: 'Open Sans',
                fontSize: 46,
                fontWeight: '600',
                fill: '#ffffff',
            }
        });
        deckNameText.label = 'deckNameText';
        deckNameText.layout = {
            width: 400,
            height: 70,
            position: 'absolute',
            left: 30,
            objectPosition: 'left',
        };
        deckNameContainer.addChild(deckNameText);

        const editDeckNameButton = new PIXI.Sprite(PIXI.Assets.get('editDeckName'));
        editDeckNameButton.label = 'editDeckNameButton';
        editDeckNameButton.layout = {
            width: 45,
            height: 45,
            position: 'absolute',
            right: 20,
        };
        deckNameContainer.addChild(editDeckNameButton);

        const deckCountContainer = new PIXI.Container();
        deckCountContainer.label = 'deckCountContainer';
        deckCountContainer.layout = {
            width: 200,
            height: 70,
            right: 50,
            position: 'absolute',
        };
        this.topBarContainer.addChild(deckCountContainer);

        const deckCountBackground = new PIXI.Graphics();
        deckCountBackground.label = 'deckCountBackground';
        deckCountBackground.x = 0;
        deckCountBackground.y = 0;
        deckCountBackground.roundRect(0, 0, 200, 70, 50);
        deckCountBackground.fill({ color: '#0d1119' });
        deckCountBackground.stroke({ color: '#6d7e99', width: 3 });
        deckCountContainer.addChild(deckCountBackground);

        const deckCountText = new PIXI.Text({
            text: '00 / 50',
            style: {
                fontFamily: 'Open Sans',
                fontSize: 48,
                fill: '#ffffff',
                fontWeight: 'bold',
                letterSpacing: 4,
            }
        });
        deckCountText.label = 'deckCountText';
        deckCountText.x = 100;
        deckCountText.y = 35;
        deckCountText.anchor.set(0.5);
        deckCountContainer.addChild(deckCountText);
    }

    buildMainLayout() {
        this.container.removeChildren();

        this.topBarContainer = new LayoutContainer();
        this.topBarContainer.label = 'topBarContainer';
        this.topBarContainer.x = 0;
        this.topBarContainer.y = 0;
        this.topBarContainer.layout = {
            width: APP_WIDTH,
            height: TOP_BAR_HEIGHT,
            backgroundColor: '#324464',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            // padding: 20,
        };
        this.container.addChild(this.topBarContainer);

        this.leaderAreaContainer = new LayoutContainer();
        this.leaderAreaContainer.label = 'leaderAreaContainer';
        this.leaderAreaContainer.x = 0;
        this.leaderAreaContainer.y = TOP_BAR_HEIGHT;
        this.leaderAreaContainer.layout = {
            width: LEADER_AREA_WIDTH,
            height: TOP_HALF_HEIGHT,
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 20,
            backgroundColor: '#203250',
        };
        this.container.addChild(this.leaderAreaContainer);

        const deckCardsListContainer = new LayoutContainer();
        deckCardsListContainer.label = 'deckCardsListContainer';
        deckCardsListContainer.x = LEADER_AREA_WIDTH;
        deckCardsListContainer.y = TOP_BAR_HEIGHT;
        deckCardsListContainer.layout = {
            width: DECK_CARDS_LIST_CONTAINER_WIDTH,
            height: TOP_HALF_HEIGHT,
            flexDirection: 'column',
            alignContent: 'flex-start',
            overflow: 'scroll',
            gap: 20,
            padding: 20,
            flexWrap: 'wrap',
            justifyContent: 'center',
            backgroundColor: '#0d1119',
        };
        this.container.addChild(deckCardsListContainer);

        this.buildTopBar();

        this.quickFiltersContainer = new LayoutContainer();
        this.quickFiltersContainer.label = 'quickFiltersContainer';
        this.quickFiltersContainer.x = 0;
        this.quickFiltersContainer.y = TOP_BAR_HEIGHT + TOP_HALF_HEIGHT;
        this.quickFiltersContainer.layout = {
            width: APP_WIDTH,
            height: QUICK_FILTERS_HEIGHT,
            backgroundColor: '#324464',
            borderColor: '#131c2b',
            justifyContent: 'center',
            alignItems: 'center',
        };
        this.container.addChild(this.quickFiltersContainer);

        const quickFiltersBottomBorder = new PIXI.Graphics();
        quickFiltersBottomBorder.label = 'quickFiltersBottomBorder';
        quickFiltersBottomBorder.x = 0;
        quickFiltersBottomBorder.y = TOP_BAR_HEIGHT + TOP_HALF_HEIGHT + QUICK_FILTERS_HEIGHT;
        quickFiltersBottomBorder.rect(0, 0, APP_WIDTH, QUICK_FILTERS_BOTTOM_BORDER_HEIGHT);
        quickFiltersBottomBorder.fill({ color: '#131c2b' });
        this.container.addChild(quickFiltersBottomBorder);

        this.collectionCardsListContainer = new LayoutContainer();
        this.collectionCardsListContainer.label = 'collectionCardsListContainer';
        this.collectionCardsListContainer.x = 0;
        this.collectionCardsListContainer.y = TOP_BAR_HEIGHT + TOP_HALF_HEIGHT + QUICK_FILTERS_HEIGHT + QUICK_FILTERS_BOTTOM_BORDER_HEIGHT;
        this.collectionCardsListContainer.layout = {
            width: APP_WIDTH,
            height: COLLECTION_CARDS_LIST_CONTAINER_HEIGHT,
            flexDirection: 'row',
            alignContent: 'flex-start',
            overflow: 'scroll',
            gap: 30,
            padding: 40,
            paddingBottom: 270,
            flexWrap: 'wrap',
            justifyContent: 'center',
            backgroundColor: '#203250',
        };
        this.container.addChild(this.collectionCardsListContainer);
        
        if (this.mode === 'leaderSelection') {
            const chooseLeaderText = new PIXI.Text({
                text: 'Select a Leader to Start',
                style: {
                    fontFamily: 'Open Sans',
                    fontSize: 50,
                    fill: '#7a88a2',
                    fontWeight: '600'
                }
            });
            chooseLeaderText.label = 'chooseLeaderText';
            chooseLeaderText.x = LEADER_AREA_WIDTH + DECK_CARDS_LIST_CONTAINER_WIDTH / 2;
            chooseLeaderText.y = TOP_BAR_HEIGHT + TOP_HALF_HEIGHT / 2;
            chooseLeaderText.anchor.set(0.5);
            this.container.addChild(chooseLeaderText);
        }

        this.rebuildLeaderArea();
        this.rebuildQuickFiltersContainer();
        this.rebuildCollectionCardsListContainer();
        this.rebuildDeckCardsListContainer();
        this.rebuildDeckCountText();

        const buttonsContainer = new LayoutContainer();
        buttonsContainer.label = 'buttonsContainer';
        buttonsContainer.x = 0;
        buttonsContainer.y = APP_HEIGHT - 150 - 75;
        buttonsContainer.layout = {
            width: APP_WIDTH,
            height: 150,
            flexDirection: 'row',
            justifyContent: 'center',
            gap: 50,
        };
        this.container.addChild(buttonsContainer);

        const saveButtonContainer = new PIXI.Container();
        saveButtonContainer.label = 'saveButtonContainer';
        saveButtonContainer.tint = 0x808080;
        saveButtonContainer.layout = {
            width: 400,
            height: 150,
        };

        saveButtonContainer.eventMode = 'static';
        saveButtonContainer.on('pointerdown', () => {
            this.saveDeck();
        });

        const saveButton = new PIXI.Sprite(PIXI.Assets.get('saveDeck'));
        saveButton.label = 'saveButton';
        saveButton.layout = {
            width: 400,
            height: 150,
        };
        saveButtonContainer.addChild(saveButton);

        buttonsContainer.addChild(saveButtonContainer);

        // Add a cancel button next to the save button
        const cancelButtonContainer = new PIXI.Container();
        cancelButtonContainer.label = 'cancelButtonContainer';
        cancelButtonContainer.layout = {
            width: 400,
            height: 150,
        };

        cancelButtonContainer.eventMode = 'static';
        cancelButtonContainer.on('pointerdown', () => {
            this.cancel();
        });

        const cancelButton = new PIXI.Sprite(PIXI.Assets.get('cancelDeck'));
        cancelButton.label = 'cancelButton';
        cancelButton.layout = {
            width: 400,
            height: 150,
        };
        cancelButtonContainer.addChild(cancelButton);

        buttonsContainer.addChildAt(cancelButtonContainer);

        this.cardInfoContainer = new CardInfoContainer();
        this.cardInfoContainer.label = 'cardInfoContainer';
        this.cardInfoContainer.x = APP_WIDTH / 2;
        this.cardInfoContainer.y = APP_HEIGHT / 2;
        this.cardInfoContainer.visible = false;
        this.cardInfoContainer.onCloseClicked = () => {
            this.cardInfoContainer.visible = false;
        };
        this.container.addChild(this.cardInfoContainer);

        this.buildConfirmSwapModal();
    }

    rebuildLeaderArea() {
        this.leaderAreaContainer.removeChildren();

        const scaleFactor = 1.6;

        if (!this.selectedLeader) {
            const leaderBackface = new TappableCard(null, { scaleFactor, isLeaderBackface: true });
            this.leaderAreaContainer.addChild(leaderBackface);
        } else {
            const leader = new TappableCard(this.selectedLeader, { scaleFactor });
            leader.onHeld = () => {
                this.cardInfoContainer.update(this.selectedLeader);
                this.cardInfoContainer.visible = true;
            };
            this.leaderAreaContainer.addChild(leader);
        }

        if (this.mode === 'leaderSelection') {
            const confirmLeaderButton = new PIXI.Sprite(PIXI.Assets.get('confirmLeader'));
            confirmLeaderButton.label = 'confirmLeaderButton';
            confirmLeaderButton.layout = {
                width: CARD_WIDTH * scaleFactor,
            };
            confirmLeaderButton.tint = this.selectedLeader ? '#ffffff' : '#555555';
            confirmLeaderButton.eventMode = 'static';
            confirmLeaderButton.on('pointerdown', () => {
                this.confirmLeader();
            });
            this.leaderAreaContainer.addChild(confirmLeaderButton);
        } else if (this.mode === 'addingCardsToDeck') {
            const swapLeaderButton = new PIXI.Sprite(PIXI.Assets.get('swapLeader'));
            swapLeaderButton.label = 'swapLeaderButton';
            swapLeaderButton.layout = {
                width: CARD_WIDTH * scaleFactor,
            };
            swapLeaderButton.eventMode = 'static';
            swapLeaderButton.on('pointerdown', () => {
                if (this.deckCardsCount() === 0) {
                    this.swapLeader();
                } else {
                    this.showConfirmSwapModal();
                }                
            });
            this.leaderAreaContainer.addChild(swapLeaderButton);
        }
    }

    rebuildQuickFiltersContainer() {
        this.quickFiltersContainer.removeChildren();

        const onChange = (selected, key) => {
            this[key] = selected;
            this.rebuildCollectionCardsListContainer();
        };

        if (this.mode === 'leaderSelection') {
            this.quickFiltersContainer.addChild(new ColorFiltersRow({
                width: APP_WIDTH,
                height: QUICK_FILTERS_HEIGHT,
                multiple: false,
                selected: this.selectedColors,
                onChange: (selected) => onChange(selected, 'selectedColors'),
            }));
        } else if (this.mode === 'addingCardsToDeck') {
            this.quickFiltersContainer.addChild(new CostFiltersRow({
                width: APP_WIDTH,
                height: QUICK_FILTERS_HEIGHT,
                multiple: false,
                selected: this.selectedCosts,
                onChange: (selected) => onChange(selected, 'selectedCosts'),
            }));
        }
    }

    rebuildCollectionCardsListContainer() {
        this.collectionCardsListContainer.removeChildren();
        if (this.mode === 'leaderSelection') {
            let leaders = this.collection.filter(card => card.type === 'Leader');
            leaders = leaders.filter((leader) => cardMatchesColorFilter(leader, this.selectedColors));
            const scaleFactor = 1.6;
            for (const leader of leaders) {
                const tappableCard = new TappableCard(leader, { scaleFactor });
                tappableCard.onCardTap = () => {
                    this.selectLeader(leader);
                };
                tappableCard.onHeld = () => {
                    this.cardInfoContainer.update(leader);
                    this.cardInfoContainer.visible = true;
                };
                this.collectionCardsListContainer.addChild(tappableCard);
            }

        
            const numLeaders = leaders.length;
            const remainder = numLeaders % 3;
            if (remainder !== 0) {
                const emptiesToAdd = 3 - remainder;
                for (let i = 0; i < emptiesToAdd; i++) {
                    // Add invisible or empty element to keep the grid layout.
                    const emptyLeaderSlot = new PIXI.Container();
                    emptyLeaderSlot.label = 'emptyLeaderSlot';
                    emptyLeaderSlot.layout = {
                        width: CARD_WIDTH * scaleFactor,
                        height: CARD_HEIGHT * scaleFactor,
                    };
                    
                    this.collectionCardsListContainer.addChild(emptyLeaderSlot);
                }
            }
        } else if (this.mode === 'addingCardsToDeck') {
            const leaderColors = [].concat(this.selectedLeader?.color ?? []);
            let cards = this.collection.filter((card) => {
                if (card.type === 'Leader' || !card.color || leaderColors.length === 0) return false;
                return [].concat(card.color).some((color) => leaderColors.includes(color));
            });
            cards = cards.filter((card) => cardMatchesCostFilter(card, this.selectedCosts));
            const scaleFactor = 1.6;
            for (const card of cards) {
                const currentCount = this.deckCards[card.id]?.count || 0;
                const tappableCard = new TappableCard(card, {
                    scaleFactor,
                    countersMode: 'editor',
                    selected: currentCount,
                    available: this.availableCopiesOf(card),
                    atLimit: !this.canAddMore(card),
                });
                tappableCard.onCardTap = () => {
                    this.addCard(card);
                };
                tappableCard.onHeld = () => {
                    this.cardInfoContainer.update(card);
                    this.cardInfoContainer.visible = true;
                };
                this.collectionCardsListContainer.addChild(tappableCard);
            }

            const numCards = cards.length;
            const remainder = numCards % 3;
            if (remainder !== 0) {
                const emptiesToAdd = 3 - remainder;
                for (let i = 0; i < emptiesToAdd; i++) {
                    // Add invisible or empty element to keep the grid layout.
                    const emptyCardSlot = new PIXI.Container();
                    emptyCardSlot.label = 'emptyLeaderSlot';
                    emptyCardSlot.layout = {
                        width: CARD_WIDTH * scaleFactor,
                        height: CARD_HEIGHT * scaleFactor,
                    };
                    
                    this.collectionCardsListContainer.addChild(emptyCardSlot);
                }
            }
        }

        this.collectionCardsListContainer._trackpad.yAxis._hasStopped = false;
    }

    rebuildDeckCardsListContainer() {
        const deckCardsListContainer = this.container.getChildByLabel('deckCardsListContainer');
        deckCardsListContainer.removeChildren();
        const scaleFactor = 0.90;
        for (const cardId in this.deckCards) {
            const card = this.deckCards[cardId].card;
            if (!card) continue;
            const tappableCard = new TappableCard(card, {
                scaleFactor,
                countersMode: 'editor',
                selected: this.deckCards[cardId].count,
                available: this.availableCopiesOf(card),
                atLimit: !this.canAddMore(card),
            });
            tappableCard.onCardTap = () => {
                this.removeCard(card);
            };
            tappableCard.onHeld = () => {
                this.cardInfoContainer.update(card);
                this.cardInfoContainer.visible = true;
            };
            deckCardsListContainer.addChild(tappableCard);
        }

        const numCards = Object.keys(this.deckCards).length;
        if (numCards % 2 !== 0) {
            const emptyCardSlot = new PIXI.Container();
            emptyCardSlot.label = 'emptyCardSlot';
            emptyCardSlot.layout = {
                width: CARD_WIDTH * scaleFactor,
                height: CARD_HEIGHT * scaleFactor,
            };
            deckCardsListContainer.addChild(emptyCardSlot);
        }
    }

    rebuildDeckCountText() {
        const deckCountText = this.container.getChildByLabel('deckCountText', true);
        deckCountText.text = `${Object.values(this.deckCards).reduce((acc, card) => acc + card.count, 0).toString().padStart(2, '0')}/50`;
    }

    deckCardsCount() {
        return Object.values(this.deckCards).reduce((acc, card) => acc + card.count, 0);
    }

    canSaveDeck() {
        if (this.isLocked) {
            return false;
        }

        const totalCards = Object.values(this.deckCards).reduce((acc, card) => acc + card.count, 0);
        return totalCards === 50;
    }

    enableDisableSaveButton() {
        const saveButtonContainer = this.container.getChildByLabel('saveButtonContainer', true);
        if (this.canSaveDeck()) {
            saveButtonContainer.tint = 0xFFFFFF;
        } else {
            saveButtonContainer.tint = 0x808080;
        }
    }

    buildCreateNewDeckPage() {
        if (this.mode === 'loading') {
            this.buildLoadingPage();
        } else if (this.mode === 'leaderSelection' || this.mode === 'addingCardsToDeck') {
            this.buildMainLayout();
        }
    }

    async setCollection(collection) {
        this.collection = collection;
        this.buildCreateNewDeckPage();
    }

    setLeader(leader) {
        this.selectedLeader = leader;
    }

    setDeckCards(deckCards) {
        this.deckCards = deckCards;
    }

    setMode(mode) {
        this.mode = mode;
    }

    // A deck can never run more copies than the player owns of this art,
    // and never more than 4 total across all arts of the same base card.
    baseCardIdOf(card) {
        return card?.baseCardId || card?.id;
    }

    deckCountForBase(baseId) {
        return Object.values(this.deckCards).reduce((sum, { count, card }) => {
            return sum + (this.baseCardIdOf(card) === baseId ? count : 0);
        }, 0);
    }

    ownedCopiesOf(card) {
        if (!card) return 0;
        return this.collection.find((c) => c.id === card.id)?.count ?? 0;
    }

    // Counter display: owned copies of this art, capped at the game's 4-per-card limit.
    availableCopiesOf(card) {
        return Math.min(this.ownedCopiesOf(card), MAX_COPIES_PER_CARD);
    }

    // True when another copy of this art can still be added (ownership + cross-art 4-of).
    canAddMore(card) {
        const currentCount = this.deckCards[card.id]?.count || 0;
        if (currentCount >= this.ownedCopiesOf(card)) {
            return false;
        }
        if (this.deckCountForBase(this.baseCardIdOf(card)) >= MAX_COPIES_PER_CARD) {
            return false;
        }
        return true;
    }

    addCard(card) {
        const totalCards = Object.values(this.deckCards).reduce((acc, card) => acc + card.count, 0);
        if (totalCards >= 50) {
            return;
        }

        if (!this.canAddMore(card)) {
            return;
        }

        const currentCount = this.deckCards[card.id]?.count || 0;
        this.deckCards[card.id] = { count: currentCount + 1, card: card };
        this.rebuildCollectionCardsListContainer();
        this.rebuildDeckCardsListContainer();
        this.rebuildDeckCountText();
        this.enableDisableSaveButton();
    }

    removeCard(card) {
        if (this.isLocked) {
            return;
        }

        const cardId = card.id;

        if (this.deckCards[cardId]) {
            if (this.deckCards[cardId].count > 1) {
                this.deckCards[cardId].count -= 1;
            } else {
                delete this.deckCards[cardId];
            }
            this.rebuildCollectionCardsListContainer();
            this.rebuildDeckCardsListContainer();
            this.rebuildDeckCountText();
            this.enableDisableSaveButton();
        }
    }

    saveDeck() {
        if (!this.canSaveDeck()) {
            return;
        }
   
        this.onSaveDeck?.(this.name, this.selectedLeader.id, this.deckCards);
    }

    cancel() {
        this.onCancel?.();
    }

    selectLeader(leader) {
        this.selectedLeader = leader;
        this.rebuildLeaderArea();
    }

    confirmLeader() {
        if (!this.selectedLeader) {
            return;
        }

        this.setMode('addingCardsToDeck');
        this.buildMainLayout();
    }

    swapLeader() {
        if (this.isLocked) {
            return;
        }

        this.setMode('leaderSelection');
        this.selectedLeader = null;
        this.deckCards = {};
        this.buildMainLayout();
    }

    setName(name) {
        this.name = name;
        const deckNameText = this.topBarContainer?.getChildByLabel('deckNameText', true);
        if (deckNameText) {
            deckNameText.slot.text = name;
            this.enableDisableSaveButton();
        }
    }
}